"use client";

import Link from "next/link";
import { useState, useEffect, useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { ChevronDown, ChevronLeft, ChevronUp, LayoutGrid } from "lucide-react";
import { useMenusContext } from "@core/helpers/client/contexts/MenusContext";
import { useAuthContext } from "@core/helpers/client/contexts/AuthContext";
import useAuth from "@core/helpers/client/hooks/useAuth";
import { mark_logout_navigation } from "@/helpers/accountAuth";
import { to_nested } from "@core/helpers/array";
import { openAuthModal } from "@/hooks/useAuthModal";
import ModalComponent from "@/components/ui/ModalComponent";
import Button from "@/components/ui/button";
import useIsDesktop from "@/hooks/useIsDesktop";
import { useServerDevice } from "@/contexts/ServerDevice";

const MenuCategoryHeader = ({ mobileOpen, setMobileOpen } = {}) => {
    const { menus, menus_loading } = useMenusContext();
    const { is_auth } = useAuthContext();
    const { logout_process } = useAuth();
    const router = useRouter();
    const pathname = usePathname() || "";
    const [openMenu, setOpenMenu] = useState(null);
    const [openCategory, setOpenCategory] = useState(null);
    const [internalOpen, setInternalOpen] = useState(false);
    const [logoutModal, setLogoutModal] = useState(false);
    const isControlled = mobileOpen !== undefined && setMobileOpen !== undefined;
    const isDrawerOpen = isControlled ? mobileOpen : internalOpen;
    const closeDrawer = () => (isControlled ? setMobileOpen(false) : setInternalOpen(false));

    // نوار دسکتاپ و کشوی موبایل هر کدام یک نسخه‌ی کامل فهرست دسته‌ها هستند؛ فقط نسخه‌ی
    // همان viewport در DOM می‌ماند و محتوای کشو از اولین باز شدن ساخته می‌شود.
    // تا hydration دستگاهِ User-Agent تصمیم می‌گیرد (تبلت و ناشناس هر دو)، مثل DeviceOnly
    const is_desktop = useIsDesktop();
    const server_device = useServerDevice();
    const show_desktop = is_desktop === null ? server_device !== "mobile" : is_desktop;
    const show_mobile = is_desktop === null ? server_device !== "desktop" : !is_desktop;
    const [drawer_used, set_drawer_used] = useState(false);
    useEffect(() => {
        if (isDrawerOpen) set_drawer_used(true);
    }, [isDrawerOpen]);
    const render_drawer_items = drawer_used || isDrawerOpen;

    const handleToggle = (index) => {
        setOpenCategory(openCategory === index ? null : index);
    };

    const categoriesItems = useMemo(() => {
        return to_nested(menus?.["category-list"]);
    }, [menus, menus_loading]);

    useEffect(() => {
        document.body.style.overflow = isDrawerOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [isDrawerOpen]);

    const handleAccountClick = (e) => {
        e.preventDefault();
        closeDrawer();
        if (is_auth) return router.push("/account/");
        return openAuthModal("/account/");
    };

    const handleLogout = () => {
        mark_logout_navigation();
        logout_process();
        setLogoutModal(false);
        closeDrawer();
        router.replace("/");
    };

    const account_links = [
        { key: "shop", title: "رفتن به فروشگاه ژومنت", href: "/", className: "text-jmt-primary-800" },
        { key: "account", title: "حساب کاربری", href: "/account/", onClick: handleAccountClick },
        { key: "favorites", title: "علاقه‌مندی‌ها", href: "/account/favorites/?active-step=blog" },
    ];

    return (
        <>
            {/* desktop */}
            {show_desktop && <nav className="hidden w-full lg:flex items-center justify-center gap-6">
                {categoriesItems?.length > 0 &&
                    categoriesItems?.map((item) => (
                        <div
                            key={item?.id}
                            className="relative"
                            onMouseEnter={() => {
                                item?.children?.length > 0 ? setOpenMenu(item.title) : {};
                            }}
                            onMouseLeave={() => setOpenMenu(null)}>
                            <Link
                                href={item?.link?.url || ""}
                                className="flex items-center text-jmt-main transition gap-1 py-4 hover:text-jmt-primary-800">
                                {item?.title}
                                {item?.children?.length > 0 && <ChevronDown size={22} />}
                            </Link>

                            <div
                                className={`absolute top-full right-0 bg-jmt-white rounded-md shadow-full-light overflow-hidden z-50 transition-all duration-300 ease-in-out origin-top-right ${openMenu === item.title
                                    ? "opacity-100 scale-100 translate-y-0 visible"
                                    : "opacity-0 scale-95 -translate-y-2 invisible"
                                    }`}>
                                <ul className="flex flex-col items-center justify-center">
                                    {item?.children?.map((link) => (
                                        <li key={link?.id}>
                                            <Link
                                                href={link?.link?.url || ""}
                                                className="block whitespace-nowrap text-sm text-jmt-main rounded-md hover:text-jmt-primary-800 transition py-3 px-8">
                                                {link?.title}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
            </nav>}

            {/* mobile: مودال بین هدر و نویگیشن فوتر مجله (هر دو دیده می‌مانند)؛ با دکمه‌ی «دسته‌بندی» از پایین باز و بسته می‌شود */}
            {show_mobile && <div
                role="dialog"
                aria-modal="true"
                aria-label="دسته‌بندی مجله"
                inert={!isDrawerOpen}
                style={{
                    top: "var(--header-height, 64px)",
                    bottom: "var(--footer-nav-height, 0px)",
                }}
                className={`lg:hidden fixed inset-x-0 z-30 flex flex-col bg-jmt-white transition-all duration-300 ease-in-out ${isDrawerOpen ? "translate-y-0 opacity-100 pointer-events-auto" : "translate-y-full opacity-0 pointer-events-none"
                    }`}>

                <div className="flex-1 overflow-y-auto">
                    <ul className="flex flex-col">
                        {render_drawer_items && categoriesItems?.length > 0 &&
                            categoriesItems.map((item, index) => {
                                const hasChildren = item?.children?.length > 0;
                                const isOpenAcc = openCategory === index;
                                const isLast = index === categoriesItems.length - 1;
                                const isActive = item?.link?.url && pathname === item?.link?.url;

                                return (

                                    <li key={item?.id} className="px-4">
                                        <div className={isLast ? "" : "border-b border-jmt-neutral-200"}>
                                            {hasChildren ? (
                                                <button
                                                    type="button"
                                                    onClick={() => handleToggle(index)}
                                                    aria-expanded={isOpenAcc}
                                                    className="w-full flex items-center justify-between py-4 text-sm text-jmt-main">
                                                    <span>{item?.title}</span>
                                                    {isOpenAcc ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                </button>
                                            ) : (
                                                <Link
                                                    href={item?.link?.url || ""}
                                                    onClick={closeDrawer}
                                                    aria-current={isActive ? "page" : undefined}
                                                    className="w-full block py-4 text-sm text-jmt-main">
                                                    {item?.title}
                                                </Link>
                                            )}

                                            {hasChildren && (
                                                <div
                                                    className={`overflow-hidden transition-all duration-300 ${isOpenAcc ? "max-h-[1000px]" : "max-h-0"
                                                        }`}>


                                                    <Link
                                                        href={item?.link?.url || ""}
                                                        onClick={closeDrawer}
                                                        aria-current={isActive ? "page" : undefined}
                                                        className="flex items-center gap-2 mx-4 mb-1 px-3 py-3 rounded-md bg-jmt-primary-900/8 text-sm text-jmt-primary-800">
                                                        <LayoutGrid size={20} />
                                                        <span> صفحه {item?.title} </span>
                                                    </Link>

                                                    <ul className="flex flex-col pb-2">
                                                        {item.children.map((link) => (
                                                            <li key={link?.id}>
                                                                <Link
                                                                    href={link?.link?.url || ""}
                                                                    onClick={closeDrawer}
                                                                    aria-current={pathname === link?.link?.url ? "page" : undefined}
                                                                    className="block px-4 py-3 text-sm text-jmt-neutral-700 aria-[current=page]:text-jmt-primary-800">
                                                                    {link?.title}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                    </ul>

                </div>
            </div>}

        </>
    );
};

export default MenuCategoryHeader;
